import { useState, useMemo, useEffect } from "react";
import { useStock } from "../App"; 
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";

// Premium SVG Icons
const Icons = {
  Wallet: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"></path><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"></path><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path></svg>,
  Plus: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
  Edit: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>,
  Lock: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>,
  History: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
};

// 🔥 HELPER: Smart text highlighter
const renderHighlightedDesc = (desc) => {
  if (!desc) return null;
  const parts = desc.split(/(Payment|Unpaid|Stock)/gi);
  return parts.map((part, i) => {
    const lower = part.toLowerCase();
    if (lower === 'payment') return <span key={i} style={{ color: '#047857', background: '#d1fae5', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>{part}</span>;
    if (lower === 'unpaid') return <span key={i} style={{ color: '#b91c1c', background: '#fee2e2', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>{part}</span>;
    if (lower === 'stock') return <span key={i} style={{ color: '#6d28d9', background: '#ede9fe', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>{part}</span>;
    return <span key={i}>{part}</span>;
  });
};

export default function SettingsPage() {
  const { balances, addBalance, sales } = useStock(); 
  
  // Modal & Tab States
  const [modalConfig, setModalConfig] = useState({ isOpen: false, action: "add", wallet: "FFC Alipay", amount: "", pin: "" });
  const [loading, setLoading] = useState(false);
  
  // Ledger Filters & Pagination
  const [ledgerTab, setLedgerTab] = useState("All");
  const [ledgerPage, setLedgerPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setLedgerPage(1); // Reset page on tab change
  }, [ledgerTab]);

  // --- SMART LEDGER CALCULATION ---
  const { paginatedLedger, totalPages, totalItems } = useMemo(() => {
    let events = [];

    // 1. Generate all chronological events correctly
    sales.forEach(s => {
      const amount = s.amountNum || 0;
      
      if (s.status === "Expense") {
        events.push({ ...s, eventDate: s.date, eventTime: s.time, type: "Expense", desc: `Expense/Stock (${s.itemsSummary}) - Paid via ${s.wallet}`, impact: -amount, w: s.wallet, impactClass: "negative" });
      } 
      else if (s.status === "Stock") {
        events.push({ ...s, eventDate: s.date, eventTime: s.time, type: "Stock", desc: `Stock Added: ${s.itemsSummary}`, impact: -amount, w: s.wallet, impactClass: "negative" });
      }
      else if (s.status === "Deposit") {
        events.push({ ...s, eventDate: s.date, eventTime: s.time, type: "Deposit", desc: s.itemsSummary, impact: amount, w: s.wallet, impactClass: amount >= 0 ? "positive" : "negative" });
      } 
      else if (s.status === "Loss") {
        events.push({ ...s, eventDate: s.date, eventTime: s.time, type: "Loss", desc: `Loss: ${s.itemsSummary}`, impact: -amount, w: "N/A", impactClass: "negative" });
      } 
      else if (s.status === "Unpaid") {
        events.push({ ...s, eventDate: s.date, eventTime: s.time, type: "Unpaid", desc: `Unpaid Sale Created (${s.customer})`, impact: amount, w: "Unpaid", impactClass: "warning" });
      } 
      else if (s.status === "Completed") {
        if (s.paidFromUnpaid) {
          // Event 1: When it was first created as Unpaid
          events.push({ ...s, eventDate: s.date, eventTime: s.time, type: "Unpaid_Created", desc: `Unpaid Sale Created (${s.customer})`, impact: amount, w: "Unpaid", impactClass: "warning" });
          // Event 2: When the Unpaid was actually Paid
          events.push({ ...s, eventDate: s.paidDate || s.date, eventTime: s.paidTime || s.time, type: "Unpaid_Paid", desc: `Payment Received for Unpaid (${s.customer})`, impact: amount, w: s.wallet, impactClass: "positive" });
        } else {
          events.push({ ...s, eventDate: s.date, eventTime: s.time, type: "Sale", desc: `Product Sale (${s.customer})`, impact: amount, w: s.wallet, impactClass: "positive" });
        }
      }
    });

    // 2. Sort events Newest to Oldest for calculation
    events.sort((a, b) => {
      const timeA = new Date(`${a.eventDate} ${a.eventTime || "12:00:00 AM"}`);
      const timeB = new Date(`${b.eventDate} ${b.eventTime || "12:00:00 AM"}`);
      return timeB - timeA; 
    });

    // 3. Calculate running snapshot balances dynamically
    // Start with the CURRENT actual balances
    let rAlipay = balances["FFC Alipay"] || 0;
    let rWechat = balances["FFC Wechat"] || 0;
    let rUnpaid = sales.filter(s => s.status === "Unpaid").reduce((acc, s) => acc + (s.amountNum || 0), 0);

    // Loop backwards in time to calculate history
    events.forEach(e => {
      // Step A: Save the running balance AT THIS MOMENT to the event
      e.snapAlipay = rAlipay;
      e.snapWechat = rWechat;
      e.snapUnpaid = rUnpaid;

      // Step B: Reverse the impact to get the balance BEFORE this event happened
      if (e.w === "FFC Alipay") rAlipay -= e.impact;
      if (e.w === "FFC Wechat") rWechat -= e.impact;

      // Reverse Unpaid Logic correctly
      if (e.type === "Unpaid_Created" || e.type === "Unpaid") {
        // This transaction added to the unpaid balance, so going backwards we subtract it
        rUnpaid -= e.impact; 
      }
      else if (e.type === "Unpaid_Paid") {
        // This transaction cleared an unpaid balance, so going backwards the unpaid amount was higher
        rUnpaid += e.impact; 
      }
    });

    // 4. Apply Cutoff Date & Tab Filters
    let filtered = events.filter(e => e.eventDate >= "2026-04-22");

    if (ledgerTab !== "All") {
      filtered = filtered.filter(e => {
        if (ledgerTab === "Unpaid") return e.w === "Unpaid" || e.type.includes("Unpaid");
        return e.w === ledgerTab;
      });
    }

    // 5. Pagination
    const tPages = Math.ceil(filtered.length / itemsPerPage) || 1;
    const vPage = Math.min(ledgerPage, tPages);
    const paginated = filtered.slice((vPage - 1) * itemsPerPage, vPage * itemsPerPage);

    return { paginatedLedger: paginated, totalPages: tPages, totalItems: filtered.length };
  }, [sales, balances, ledgerTab, ledgerPage]);

  const handleFundSubmit = async (e) => {
    e.preventDefault();
    
    // 🔥 SECURITY PIN CHECK
    if (modalConfig.pin !== "0887") {
      return alert("Incorrect Security PIN! Authorization Denied.");
    }

    const amount = parseFloat(modalConfig.amount);
    if (isNaN(amount) || amount < 0) return alert("Please enter a valid amount.");

    setLoading(true);
    try {
      const difference = modalConfig.action === "add" ? amount : (amount - (balances[modalConfig.wallet] || 0));
      
      await addBalance(modalConfig.wallet, difference); 

      if (difference !== 0) {
        const now = new Date();
        await addDoc(collection(db, "sales"), {
          id: `#DEP-${Math.floor(1000 + Math.random() * 9000)}`,
          customer: "Admin",
          itemsSummary: modalConfig.action === "add" ? "Added Money to Wallet" : "Manual Balance Edit",
          amount: `¥${Math.abs(difference).toLocaleString(undefined, {minimumFractionDigits: 2})}`,
          amountNum: difference,
          status: "Deposit",
          date: now.toISOString().split('T')[0],
          time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
          wallet: modalConfig.wallet,
          createdAt: now
        });
      }
      
      setModalConfig({ ...modalConfig, isOpen: false, amount: "", pin: "" });
      alert("Wallet updated successfully!");
    } catch (err) {
      alert("Error updating wallet: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (action) => setModalConfig({ ...modalConfig, action, isOpen: true, amount: action === "edit" ? balances[modalConfig.wallet] || "" : "", pin: "" });

  const cardStyle = { background: "#FFFFFF", borderRadius: 20, border: "1px solid #e2e8f0", padding: "32px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, paddingBottom: 40 }}>
      
      {/* Header Area */}
      <div>
        <h2 style={{ margin: "0 0 4px", color: "#0f172a", fontSize: 24, fontWeight: 800, letterSpacing: "-0.5px" }}>Settings & Preferences</h2>
        <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>Manage your workspace, wallet balances, and track history.</p>
      </div>

      {/* --- Static Profile Block --- */}
      <div style={{ ...cardStyle, display: "flex", alignItems: "center", gap: 24 }}>
        <div style={{ width: 84, height: 84, borderRadius: "50%", background: "linear-gradient(135deg, #4f46e5, #9333ea)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 800, color: "#FFFFFF", boxShadow: "0 10px 15px -3px rgba(79, 70, 229, 0.2)", flexShrink: 0 }}>
          F
        </div>
        <div>
          <h3 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: "0 0 6px" }}>FFC</h3>
          <p style={{ fontSize: 14, color: "#64748b", margin: 0, fontWeight: 500 }}>
            <span style={{ color: "#4f46e5", background: "#eef2ff", padding: "3px 10px", borderRadius: 6 }}>Administrator</span> Workspace
          </p>
        </div>
      </div>

      {/* --- Wallet Management Section --- */}
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: "#6366f1" }}>{Icons.Wallet}</span>
            <h4 style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", margin: 0 }}>Wallet Management</h4>
          </div>
          <button onClick={() => openModal("edit")} style={{ background: "transparent", border: "none", color: "#64748b", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600 }}>
            {Icons.Edit} Edit Balances
          </button>
        </div>

        <div className="grid-2" style={{ marginBottom: 16 }}>
          {["FFC Alipay", "FFC Wechat"].map(wallet => (
            <div key={wallet} style={{ background: "#f8fafc", padding: "20px", borderRadius: 16, border: "1px solid #f1f5f9" }}>
              <p style={{ margin: "0 0 6px 0", fontSize: 13, color: "#64748b", fontWeight: 600 }}>{wallet} Balance</p>
              <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "#0f172a", fontFamily: "system-ui" }}>¥{balances[wallet]?.toLocaleString(undefined, {minimumFractionDigits: 2}) || "0.00"}</p>
            </div>
          ))}
        </div>

        <button onClick={() => openModal("add")} style={{ width: "100%", padding: "14px", background: "#eef2ff", color: "#4f46e5", border: "none", borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {Icons.Plus} Add Funds to Wallet
        </button>
      </div>

      {/* --- MASTER WALLET LEDGER --- */}
      <div style={cardStyle}>
        
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ color: "#10b981" }}>{Icons.History}</span>
            <div>
              <h4 style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", margin: 0 }}>Wallet Activity Ledger</h4>
              <p style={{ margin: "4px 0 0 0", fontSize: 13, color: "#64748b" }}>Transactions from April 22, 2026 onwards</p>
            </div>
          </div>
          
          {/* Section Tabs */}
          <div style={{ display: "flex", gap: 6, background: "#f8fafc", padding: 6, borderRadius: 10, border: "1px solid #e2e8f0" }}>
            {["All", "FFC Alipay", "FFC Wechat", "Unpaid"].map((tab) => (
              <button 
                key={tab} 
                onClick={() => setLedgerTab(tab)}
                style={{ 
                  padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none",
                  background: ledgerTab === tab ? "#ffffff" : "transparent",
                  color: ledgerTab === tab ? "#0f172a" : "#64748b",
                  boxShadow: ledgerTab === tab ? "0 1px 3px rgba(0,0,0,0.1)" : "none"
                }}
              >
                {tab.replace("FFC ", "")}
              </button>
            ))}
          </div>
        </div>

        <div className="table-responsive" style={{ minHeight: "350px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
            <thead style={{ background: "#FFFFFF", zIndex: 10 }}>
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                {["Date & Time", "Transaction Details", "Wallet Affected", "Amount", "Alipay Bal.", "WeChat Bal.", "Unpaid Bal."].map((h) => (
                  <th key={h} style={{ textAlign: "left", fontSize: 11, color: "#64748b", fontWeight: 600, padding: "10px 8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedLedger.length > 0 ? paginatedLedger.map((item, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #f8fafc", transition: "background 0.2s" }} onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "14px 8px", fontSize: 12, color: "#64748b" }}>
                    <div style={{ fontWeight: 600, color: "#0f172a" }}>{item.eventDate}</div>
                    <div style={{ fontSize: 10 }}>{item.eventTime}</div>
                  </td>
                  
                  {/* 🔥 HIGHLIGHTED DESCRIPTION */}
                  <td style={{ padding: "14px 8px", fontSize: 13, color: "#0f172a", fontWeight: 500, maxWidth: 220, whiteSpace: "normal" }}>
                    {renderHighlightedDesc(item.desc)}
                  </td>

                  <td style={{ padding: "14px 8px", fontSize: 12, color: "#64748b", fontWeight: 600 }}>{item.w}</td>
                  <td style={{ padding: "14px 8px", fontSize: 13, fontWeight: 800, color: item.impactClass === "positive" ? "#10b981" : item.impactClass === "negative" ? "#ef4444" : "#f59e0b" }}>
                    {item.impactClass === "negative" ? "-" : "+"}¥{Math.abs(item.impact).toLocaleString()}
                  </td>
                  <td style={{ padding: "14px 8px", fontSize: 13, color: "#6366f1", fontWeight: 700, fontFamily: "system-ui" }}>
                    ¥{item.snapAlipay.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </td>
                  <td style={{ padding: "14px 8px", fontSize: 13, color: "#6366f1", fontWeight: 700, fontFamily: "system-ui" }}>
                    ¥{item.snapWechat.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </td>
                  <td style={{ padding: "14px 8px", fontSize: 13, color: "#ef4444", fontWeight: 700, fontFamily: "system-ui" }}>
                    ¥{item.snapUnpaid.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>No wallet activity found for this filter.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Ledger Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, paddingTop: 16, borderTop: "1px solid #f1f5f9" }}>
            <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
              Showing Page <strong style={{ color: "#0f172a" }}>{ledgerPage}</strong> of <strong style={{ color: "#0f172a" }}>{totalPages}</strong> ({totalItems} records)
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setLedgerPage(p => Math.max(p - 1, 1))} disabled={ledgerPage === 1} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: ledgerPage === 1 ? "#f8fafc" : "#ffffff", color: ledgerPage === 1 ? "#cbd5e1" : "#0f172a", cursor: ledgerPage === 1 ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 13 }}>
                Prev
              </button>
              <button onClick={() => setLedgerPage(p => Math.min(p + 1, totalPages))} disabled={ledgerPage === totalPages} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: ledgerPage === totalPages ? "#f8fafc" : "#ffffff", color: ledgerPage === totalPages ? "#cbd5e1" : "#0f172a", cursor: ledgerPage === totalPages ? "not-allowed" : "pointer", fontWeight: 600, fontSize: 13 }}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* --- Fund Modal --- */}
      {modalConfig.isOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
          <div style={{ background: "#FFFFFF", padding: 32, borderRadius: 20, width: "100%", maxWidth: 400, boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}>
            
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: modalConfig.action === "add" ? "#eef2ff" : "#fef3c7", color: modalConfig.action === "add" ? "#4f46e5" : "#d97706", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {modalConfig.action === "add" ? Icons.Plus : Icons.Edit}
              </div>
              <h3 style={{ margin: 0, color: "#0f172a", fontSize: 20, fontWeight: 800 }}>{modalConfig.action === "add" ? "Add Funds" : "Edit Wallet"}</h3>
            </div>

            <form onSubmit={handleFundSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 6 }}>Select Wallet</label>
                <select 
                  value={modalConfig.wallet} 
                  onChange={e => setModalConfig({ ...modalConfig, wallet: e.target.value, amount: modalConfig.action === "edit" ? balances[e.target.value] : "" })} 
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #cbd5e1", outline: "none", background: "#f8fafc", fontSize: 14, fontWeight: 600, color: "#0f172a" }}
                >
                  <option value="FFC Alipay">FFC Alipay</option>
                  <option value="FFC Wechat">FFC Wechat</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 6 }}>{modalConfig.action === "add" ? "Amount to Add (¥)" : "Set New Balance (¥)"}</label>
                <input 
                  type="number" step="any" required placeholder="0.00" 
                  value={modalConfig.amount} onChange={e => setModalConfig({ ...modalConfig, amount: e.target.value })} 
                  style={{ width: "100%", padding: "14px", borderRadius: 10, border: "2px solid #e2e8f0", outline: "none", fontSize: 18, fontWeight: 700, fontFamily: "system-ui", color: "#0f172a" }} 
                  onFocus={e => e.target.style.borderColor = "#6366f1"} onBlur={e => e.target.style.borderColor = "#e2e8f0"} 
                />
              </div>

              {/* SECURITY PIN INPUT */}
              <div>
                <label style={{ fontSize: 12, color: "#ef4444", fontWeight: 700, display: "flex", alignItems: "center", gap: 4, marginBottom: 6 }}>
                  {Icons.Lock} Security PIN Required
                </label>
                <input 
                  type="password" required maxLength="4" placeholder="••••" 
                  value={modalConfig.pin} onChange={e => setModalConfig({ ...modalConfig, pin: e.target.value })} 
                  style={{ width: "100%", padding: "14px", borderRadius: 10, border: "2px solid #fecaca", outline: "none", fontSize: 20, fontWeight: 800, letterSpacing: "4px", color: "#b91c1c", textAlign: "center", background: "#fef2f2" }} 
                  onFocus={e => e.target.style.borderColor = "#ef4444"} onBlur={e => e.target.style.borderColor = "#fecaca"} 
                />
              </div>

              <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
                <button type="button" onClick={() => setModalConfig({ ...modalConfig, isOpen: false, pin: "" })} style={{ flex: 1, padding: "14px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#FFFFFF", cursor: "pointer", fontWeight: 700, color: "#64748b" }} disabled={loading}>Cancel</button>
                <button type="submit" style={{ flex: 1, padding: "14px", borderRadius: 10, border: "none", background: modalConfig.action === "add" ? "#4f46e5" : "#f59e0b", color: "#FFFFFF", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }} disabled={loading}>
                  {loading ? "Processing..." : modalConfig.action === "add" ? "Add Funds" : "Update Balance"}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
    </div>
  );
}